// @flow
import * as React from "react"
import { MainMediaScreen } from "./_mainMediaScreen"
import ButtonsLocalView from "./buttonsLocalView"
import CornerSettingButton from "./_cornerSettingButton"
import { Flex, Box } from "@chakra-ui/react"
import {
  useRecoilValue,
  useRecoilState,
  useSetRecoilState,
  useResetRecoilState
} from "recoil"
import layoutStates from "../../recoil/atoms/layout"
import { useResizeObserver } from "../../hooks/useResizeObserver"
import { playerPropsSelector } from "../../recoil/atoms/player"
import { findSideRatio } from "../../util/findSideRatio"
import { usersListSmallWindowSelector } from "../../recoil/atoms/userListSmallWindow"
import useWindowSize from "../../hooks/useWindowSize"
import { useLocalUserType } from "../../hooks/useLocalUserType"
import {
  badgeHistoryListSelector,
  badgesHistoryFilterState
} from "../../recoil/atoms/badgeHistory"
import { localUserIdAtom, usersSelector } from "../../recoil/atoms/users"
import { mainWindowUserSelector } from "../../recoil/atoms/mainWindowUser"
import { AuctionOverlayBid } from "../servicesPanel/auctions/auction-overlay-bid"
import {
  auctionParamsSelector,
  auctionFinalResultAtom
} from "../../recoil/atoms/auction"
import { isButtonHidden } from "../../util"
import { AuctionFinalOverlay } from "../servicesPanel/auctions/auction-final-overlay"
import { viewDependingLayoutAtom } from "../../recoil/atoms/view"
import { MainVideoScreen } from "./_mainVideoScreen"
import { InteractivePanel } from "./_interactivePanel"
import { useWatchParty } from "../../hooks/useWatchParty"
import useLocalUserHasPermissions from "../../hooks/useLocalUserHasPermissions"
import { shoppableMNPVideoAtom } from "../../recoil/atoms/shoppableVideos"
import {
  taggedBadgesFilterState,
  taggedBadgesListSelector
} from "../../recoil/atoms/taggedBadges"
import FloatingContainer from "./floating-container"
import { productPageOverlaySelector } from "../../recoil/atoms/product-overlay"
import { PRODUCT_DESTINATION } from "../../constants/enums"
import {
  audienceTotalCountSelector,
  broadcasterTotalCountSelector,
  moderatorTotalCountSelector
} from "../../recoil/atoms/userCounts"
import { brandingSelector } from "../../recoil/atoms/room"
import { trulienceAtom } from "../../recoil/atoms/trulience"
import { useVideoPopup } from "./floating-container/useVideoPopup"
import { withErrorBoundary } from "react-error-boundary"
import ComponentFallback from "../common/errors/componentFallback"

const UserPermission = window.MeetOnline?.UserPermission

export const MediaPanel = withErrorBoundary(
  React.memo(() => {
    const { isAudience, isBroadcaster, isModerator } = useLocalUserType()
    const { screenSize } = useWindowSize()

    const { canSeeHistory, canUseCam, canUseScreenshare, canUseMic } =
      useLocalUserHasPermissions()
    const { isWatchParty: isWP, isAudioWatchParty } = useWatchParty()
    const roomStyles = useRecoilValue(brandingSelector)

    const showAvatar = roomStyles.enableDigitalAvatar
    const isWatchParty = isWP && !showAvatar

    const isTablet = React.useMemo(
      () => screenSize.width < 768,
      [screenSize.width]
    )
    const setViewDependingLayout = useSetRecoilState(viewDependingLayoutAtom)
    const setTaggedBadgesFilter = useSetRecoilState(taggedBadgesFilterState)
    const resetBadgesHistoryFilter = useResetRecoilState(
      badgesHistoryFilterState
    )
    const productPageOnOverlay = useRecoilValue(productPageOverlaySelector)
    const { showAvatarPopupAndWPOnMVW } = useVideoPopup()

    React.useEffect(() => {
      setViewDependingLayout(prevState => ({
        ...prevState,
        isTablet: isTablet,
        isDesktop: true
      }))
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTablet])
    const mainScreenRef = React.useRef<HTMLDivElement>(null)
    const mediaPanelRef = React.useRef<HTMLDivElement>(null)

    const [, heightMediaPanel] = useResizeObserver(mediaPanelRef)
    const [width, height] = useResizeObserver(mainScreenRef)
    const setPlayerProps = useSetRecoilState(playerPropsSelector)
    const isFloatingDivActive = useRecoilValue(layoutStates.isFloatingDivActive)
    const widthMainScreen = React.useMemo(
      () => findSideRatio(height, 16, 9),
      [height]
    )

    const { isModalBid } = useRecoilValue(auctionParamsSelector)
    const { isFinalModal } = useRecoilValue(auctionFinalResultAtom)
    const [{ enabledShoppableMNP }] = useRecoilState(shoppableMNPVideoAtom)

    const [sidebarOpen] = useRecoilState<boolean>(layoutStates.openSidebar)
    const setIsBadgesListLive = useSetRecoilState<boolean>(
      layoutStates.isBadgesListLive
    )
    const setIsTransitionFromDesktopVersion = useSetRecoilState<boolean>(
      layoutStates.isTransitionFromDesktopVersion
    )
    const userIdsSmallWindows = useRecoilValue(usersListSmallWindowSelector)
    const allBadgeHistory = useRecoilValue(badgeHistoryListSelector)
    const localUserId = useRecoilValue(localUserIdAtom)
    const localUser = useRecoilValue(usersSelector(localUserId))
    const mainWindowUser = useRecoilValue(mainWindowUserSelector)
    const taggedBadgesList = useRecoilValue(taggedBadgesListSelector)
    let mainWindowUserId: any = ""
    if (mainWindowUser?.uid) {
      mainWindowUserId = mainWindowUser?.uid
    }
    const mainWindowUserDetails = useRecoilValue(
      usersSelector(mainWindowUserId?.replace("ss", "").trim())
    )

    const mainWindowUserProperties = React.useMemo(() => {
      return {
        camEnabled: mainWindowUserDetails?.camEnabled,
        // mainWindowUserId: mainWindowUserDetails?.id
        mainWindowUserId: mainWindowUser?.uid
      }
    }, [mainWindowUserDetails?.camEnabled, mainWindowUser?.uid])

    const localUserProperties = React.useMemo(() => {
      return {
        localUserId: localUser?.id,
        type: localUser?.type,
        camEnabled: localUser?.camEnabled,
        micEnabled: localUser?.micEnabled,
        permissions: localUser?.permissions,
        oldPermissions: localUser?.oldPermissions,
        mediaConnected: localUser?.mediaConnected,
        screenShareEnabled: localUser?.screenShareEnabled,
        color: localUser?.color,
        handUp: localUser?.handUp
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      localUser?.id,
      localUser?.type,
      localUser?.camEnabled,
      localUser?.micEnabled,
      localUser?.permissions,
      localUser?.oldPermissions,
      localUser?.mediaConnected,
      localUser?.screenShareEnabled,
      localUser?.handUp
    ])

    const localUserPropertiesForLocalButtons = React.useMemo(() => {
      return {
        localUserId: localUser?.id,
        type: localUser?.type,
        camEnabled: localUser?.camEnabled,
        micEnabled: localUser?.micEnabled,
        permissions: localUser?.permissions,
        mediaConnected: localUser?.mediaConnected,
        screenShareEnabled: localUser?.screenShareEnabled
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      localUser?.id,
      localUser?.type,
      localUser?.camEnabled,
      localUser?.micEnabled,
      localUser?.permissions,
      localUser?.mediaConnected,
      localUser?.screenShareEnabled
    ])

    const _stylingMediaPlayer: React.CSSProperties = React.useMemo(() => {
      // Hide media player only for video watch party and popup avatar is not enabled
      if (isWatchParty && !isAudioWatchParty && !showAvatarPopupAndWPOnMVW) {
        return { display: "none" }
      }

      return {}
    }, [isAudioWatchParty, isWatchParty])

    const lenghtSmallScreens = React.useMemo(() => {
      return userIdsSmallWindows.length
    }, [userIdsSmallWindows.length])

    const isPrevSpeakerStreams = React.useMemo<boolean>(() => {
      return lenghtSmallScreens > 0 && (!isAudience || isAudience)
    }, [isAudience, lenghtSmallScreens])

    const maxHeightMainScreenContainer = React.useMemo(() => {
      if (!isAudience && lenghtSmallScreens === 0) {
        return "100%"
      } else {
        return lenghtSmallScreens === 0
          ? "99%"
          : lenghtSmallScreens === 3
          ? "77%"
          : lenghtSmallScreens >= 4
          ? "79%"
          : "70%"
      }
    }, [isAudience, lenghtSmallScreens])

    const heightMainScreen = React.useMemo(
      () => findSideRatio(width, 9, 16),
      [width]
    )
    const maxHeightMainScreen = React.useMemo(() => {
      if (heightMainScreen < height) {
        return heightMainScreen
      }
      return height
    }, [height, heightMainScreen])

    const overlayWidth = React.useMemo(() => {
      return isTablet ? widthMainScreen * 0.8 : widthMainScreen
    }, [isTablet, widthMainScreen])

    const isSmallMainScreen = React.useMemo(() => {
      return (
        !isTablet &&
        (maxHeightMainScreen < 200 || (sidebarOpen && screenSize.width < 1024))
      )
    }, [isTablet, maxHeightMainScreen, screenSize.width, sidebarOpen])

    const isMediumAuctionBadge = React.useMemo(() => {
      return !isTablet && maxHeightMainScreen < 400 && maxHeightMainScreen > 200
    }, [isTablet, maxHeightMainScreen])

    const maxWidthOverlay = React.useMemo(() => {
      return widthMainScreen * 0.8
    }, [widthMainScreen])

    const isWithoutTitle = React.useMemo<boolean>(
      () => screenSize.width < 1024,
      [screenSize.width]
    )

    const isHiddenControls = React.useMemo<boolean>(() => {
      return isAudience || isBroadcaster
        ? isButtonHidden(
            localUserProperties?.permissions,
            UserPermission.canUseMic
          ) &&
            isButtonHidden(
              localUserProperties?.permissions,
              UserPermission.canUseCam
            ) &&
            isButtonHidden(
              localUserProperties?.permissions,
              UserPermission.canUseScreenshare
            )
        : false
    }, [isAudience, isBroadcaster, localUserProperties?.permissions])

    const showBadgesListLive = React.useMemo(() => {
      if (
        (allBadgeHistory.length > 0 ||
          (taggedBadgesList.length > 0 && enabledShoppableMNP)) &&
        !isModerator &&
        isHiddenControls &&
        !isPrevSpeakerStreams &&
        canSeeHistory
      ) {
        return true
      }
      return false
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      allBadgeHistory.length,
      taggedBadgesList.length,
      isModerator,
      isHiddenControls,
      isPrevSpeakerStreams,
      canSeeHistory,
      enabledShoppableMNP
    ])

    React.useEffect(() => {
      setIsBadgesListLive(showBadgesListLive)
      setIsTransitionFromDesktopVersion(showBadgesListLive)
      if (showBadgesListLive === true) {
        setTaggedBadgesFilter({ optionSort: "", textField: "" })
        resetBadgesHistoryFilter()
      }
    }, [
      setIsBadgesListLive,
      setIsTransitionFromDesktopVersion,
      showBadgesListLive,
      enabledShoppableMNP,
      setTaggedBadgesFilter,
      resetBadgesHistoryFilter
    ])

    React.useEffect(() => {
      if (!isWatchParty) {
        setPlayerProps({ action: "reset" })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isWatchParty])

    const maxHightBadgesListLive = React.useMemo(
      () => heightMediaPanel - height,
      [height, heightMediaPanel]
    )

    const numberOfUsers = React.useMemo<number>(
      () => userIdsSmallWindows.length,
      [userIdsSmallWindows.length]
    )

    // Enable the overlay only for tablet devices, and user having camera, microphone, or screen sharing permissions
    const isLeaveOverlayEnable = React.useMemo<boolean>(() => {
      const hasMicCamSharePermission =
        canUseMic || canUseCam || canUseScreenshare
      if (hasMicCamSharePermission && isTablet) return true
      return false
    }, [canUseCam, canUseMic, canUseScreenshare, isTablet])

    const isFloatingVideoOnParentDoc =
      productPageOnOverlay.location === PRODUCT_DESTINATION.PARENT_IFRAME

    const broadcasterCount = useRecoilValue(broadcasterTotalCountSelector)
    const moderatorCount = useRecoilValue(moderatorTotalCountSelector)
    const audienceCount = useRecoilValue(audienceTotalCountSelector)
    const alone =
      (broadcasterCount == 0 && moderatorCount == 0 && audienceCount == 1) ||
      (moderatorCount == 0 && audienceCount == 0 && broadcasterCount == 1) ||
      (broadcasterCount == 0 && audienceCount == 0 && moderatorCount <= 1)

    const trulienceState = useRecoilValue(trulienceAtom)

    const renderVideoPlayer = () => {
      return (
        <MainVideoScreen
          widthMainScreen={widthMainScreen}
          localUserProperties={localUserProperties}
          maxWidthOverlay={maxWidthOverlay}
          overlayWidth={overlayWidth}
          maxHeightMainScreen={maxHeightMainScreen}
          isWithoutTitle={
            !isTablet && (isWithoutTitle || widthMainScreen < 630)
          }
          isTablet={isTablet}
          isMediumAuctionBadge={isMediumAuctionBadge}
          isLeaveOverlayEnable={isLeaveOverlayEnable}
          isSmallMainScreen={isSmallMainScreen}
        />
      )
    }

    return (
      <>
        <Flex
          width="100%"
          flex={1}
          alignItems="center"
          direction="column"
          overflow="auto"
          maxHeight={isTablet && `max(${screenSize.height * 0.57}px, 220px)`}
          padding={isTablet && "10px"}
          ref={mediaPanelRef}
        >
          {isModalBid && <AuctionOverlayBid isMobile={false} />}
          {isFinalModal && <AuctionFinalOverlay />}

          <Box
            w="100%"
            display={
              isFloatingDivActive
                ? "flex"
                : isTablet && isWatchParty
                ? "flex"
                : "block"
            }
            justifyContent={
              ((isTablet && isWatchParty) || isFloatingDivActive) && "center"
            }
            height={heightMainScreen}
            marginBottom={isTablet ? "10px" : isPrevSpeakerStreams && "16px"}
            maxHeight={
              isFloatingVideoOnParentDoc ? "100%" : maxHeightMainScreenContainer
            }
            // position={ "relative"}
            position={isFloatingVideoOnParentDoc ? "absolute" : null}
            ref={mainScreenRef}
            minH={isFloatingDivActive ? null : isTablet ? "210px" : "200px"}
          >
            {/* Show the shared photo or video player here, if the popup avatar is enabled else in Floating Container. */}
            {showAvatarPopupAndWPOnMVW && isWatchParty && (
              <Box position={"relative"} width="full">
                {" "}
                {renderVideoPlayer()}{" "}
              </Box>
            )}

            <FloatingContainer
              height={heightMainScreen}
              width="100%"
              minHeight={isTablet ? "210px" : "200px"}
              marginBottom={
                isFloatingDivActive
                  ? "0px"
                  : isTablet
                  ? "10px"
                  : isPrevSpeakerStreams && "16px"
              }
              maxHeight={"100%"}
              position="relative"
              productProps={{ width: widthMainScreen }}
              isMobile={isTablet}
            >
              {!showAvatarPopupAndWPOnMVW &&
                isWatchParty &&
                renderVideoPlayer()}

              <MainMediaScreen
                isTablet={isTablet}
                styling={_stylingMediaPlayer}
                isWithoutTitle={
                  !isTablet && (isWithoutTitle || widthMainScreen < 630)
                }
                heightMainScreen={maxHeightMainScreen}
                widthMainScreen={widthMainScreen}
                isLeaveOverlayEnable={isLeaveOverlayEnable}
                overlayWidth={overlayWidth}
                isMediumAuctionBadge={isMediumAuctionBadge}
                isSmallMainScreen={isSmallMainScreen}
                maxWidthOverlay={maxWidthOverlay}
                showButtonsOnScreen={!isWatchParty}
                mainWindowUserProperties={mainWindowUserProperties}
                localUserProperties={localUserProperties}
              />
            </FloatingContainer>
          </Box>
          <InteractivePanel
            heightMainScreenContainer={height}
            maxHightBadgesListLive={maxHightBadgesListLive}
            localUserProperties={localUserProperties}
            widthMainScreenContainer={width}
            widthMainScreen={widthMainScreen}
            isTablet={isTablet}
            isSmallMainScreen={isSmallMainScreen}
            isPrevSpeakerStreams={isPrevSpeakerStreams}
            isHiddenControls={
              showAvatar ? !trulienceState.iframeHasLoaded : false
            }
          />
        </Flex>

        {!isTablet && (
          <>
            <CornerSettingButton />
            <ButtonsLocalView
              isAudience={isAudience}
              isHiddenControls={
                showAvatar ? !trulienceState.iframeHasLoaded : false
              }
              isBroadcaster={isBroadcaster}
              isWithoutTitle={isWithoutTitle}
              numberOfUsers={numberOfUsers}
              localUserProperties={localUserPropertiesForLocalButtons}
            />
          </>
        )}
      </>
    )
  }),
  {
    fallback: <ComponentFallback />
  }
)
